import {
    Button,
    HStack,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Stack,
    StackDivider,
    Text,
    VStack,
    type ModalProps,
} from '@chakra-ui/react';
import { ChangesSection } from './changes-section';
import type {
    PublicChangelogConfig,
    PublicChangelogRelease,
} from './public-changelog-config';
import { usePublicChangelog } from './use-public-changelog';

export interface PublicChangelogModalProps
    extends Omit<ModalProps, 'isOpen' | 'onClose' | 'children'> {
    config: PublicChangelogConfig;
    isManuallyOpen?: boolean;
    onClose?: () => void;
}

/**
 * Displays a modal presenting recent application changelogs and release notes.
 *
 * Automatically appears when new, unread releases are detected according to
 * the provided configuration, or can be triggered manually. Automatically marks releases as seen upon closing.
 *
 * @example
 * // Automatic trigger when unseen releases are detected
 * <PublicChangelogModal config={changelogConfig} />
 *
 * @example
 * // Controlled / manual trigger via state
 * const [isOpen, setIsOpen] = useState(false);
 *
 * <>
 *   <Button onClick={() => setIsOpen(true)}>What's New</Button>
 *   <PublicChangelogModal
 *     config={changelogConfig}
 *     isManuallyOpen={isOpen}
 *     onClose={() => setIsOpen(false)}
 *   />
 * </>
 */
export const PublicChangelogModal = ({
    config,
    isManuallyOpen,
    onClose,
    ...rest
}: PublicChangelogModalProps) => {
    const { hasNewReleases, markAsSeen, releases } = usePublicChangelog(config);

    const handleClose = () => {
        markAsSeen();
        onClose?.();
    };

    if (releases.length === 0) {
        return null;
    }

    return (
        <Modal
            isCentered
            scrollBehavior="inside"
            {...rest}
            size={rest.size ?? '3xl'}
            isOpen={!!isManuallyOpen || hasNewReleases}
            onClose={handleClose}
        >
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>What's New</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <VStack
                        w="100%"
                        gap={4}
                        divider={<StackDivider opacity="0.3" />}
                        alignItems="start"
                    >
                        {releases.map((release, idx) => (
                            <Release
                                key={release.version}
                                release={release}
                                isLatest={idx === 0}
                            />
                        ))}
                    </VStack>
                </ModalBody>
                <ModalFooter justifyContent="center">
                    <Button w={40} onClick={handleClose}>
                        Got it!
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

const Release = ({
    release: { version, changes, date },
    isLatest,
}: {
    release: PublicChangelogRelease;
    isLatest: boolean;
}) => {
    const boxStyles = isLatest
        ? {
              borderWidth: '1px',
              borderColor: 'inherit',
              bg: 'gray.50',
              _dark: { bg: 'blackAlpha.400', borderColor: 'blackAlpha.300' },
          }
        : {};

    return (
        <Stack p={4} gap={6} borderRadius="md" {...boxStyles}>
            <HStack gap={2} justifyContent="space-between">
                <Text fontWeight="bold" fontSize="xl">
                    Version {version}
                    {isLatest && (
                        <Text
                            as="span"
                            color="blue.500"
                            bg="blue.50"
                            fontSize="sm"
                            ml={4}
                            px={2}
                            py={1}
                            borderRadius="md"
                            _dark={{ bg: 'blue.800', color: 'blue.200' }}
                        >
                            Latest
                        </Text>
                    )}
                </Text>
                <Text opacity="0.7" fontWeight="500">
                    {date}
                </Text>
            </HStack>

            <ChangesSection title="Added" changes={changes.added} />

            <ChangesSection title="Changed" changes={changes.changed} />

            <ChangesSection title="Fixed" changes={changes.fixed} />

            <ChangesSection title="Deprecated" changes={changes.deprecated} />

            <ChangesSection title="Removed" changes={changes.removed} />

            <ChangesSection title="Security" changes={changes.security} />
        </Stack>
    );
};
